import { useState } from "react";
import { Link } from "react-router-dom";
import { Maximize2, Star } from "lucide-react";
import BlockShell from "./BlockShell";
import Lightbox from "../Lightbox";
import { absUrl } from "./media";
import { renderInline } from "./StoryChapterBlock";
import { DirectionsActions, isMapsUrl, mapsQueryFromUrl } from "../DirectionsActions";

const CtaBlock = ({ block }) => {
  const cfg = block.config || {};
  const [zoom, setZoom] = useState(null);
  const accent = cfg.accent || "#E76F51";
  const route = cfg.button_route || "/";
  const isExternal = /^https?:\/\//i.test(route);
  const image = cfg.image ? absUrl(cfg.image) : null;
  const bottomBtn = cfg.button_position === "bottom-center";
  const Btn = (
    cfg.button_variant === "white-navy" ? (
      <span className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] text-[#1E3A8A] shadow-lg hover:bg-[#F2F6FF] transition-colors whitespace-nowrap" data-testid="fest-cta-button">
        <Star className="h-4 w-4 text-[#1E3A8A] fill-[#1E3A8A]/20" /> {cfg.button_label || "Learn more"}
      </span>
    ) : (
    <span
      className="inline-flex items-center gap-2 rounded-full px-7 py-3 text-white font-semibold shadow"
      style={{ backgroundColor: accent }}
      data-testid="fest-cta-button"
    >
      {cfg.button_label || "Learn more"} →
    </span>
    )
  );
  const hasBtn = Boolean(cfg.button_label || cfg.button_route);
  const mapsAddr = isMapsUrl(route) ? mapsQueryFromUrl(route) : null;
  const link = !hasBtn ? null : mapsAddr ? (
    <DirectionsActions address={mapsAddr} accent={accent} size="md" label={cfg.button_label} testId="fest-cta-directions" />
  ) : isExternal ? (
    <a href={route} target="_blank" rel="noopener noreferrer">{Btn}</a>
  ) : (
    <Link to={route}>{Btn}</Link>
  );
  const imgEl = image && (
    <button type="button" onClick={() => setZoom(image)} className="relative group shrink-0 self-center md:self-auto cursor-zoom-in" aria-label="Enlarge flyer" data-testid="fest-cta-image">
      <img src={image} alt={cfg.button_label || block.title || ""} className="w-full max-w-xs md:w-72 lg:w-80 rounded-xl shadow-xl object-contain bg-white/40" loading="lazy" />
      <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/60 text-white text-[10px] font-semibold px-2 py-1 group-hover:bg-black/80 transition-colors"><Maximize2 className="h-3 w-3" /> Enlarge</span>
    </button>
  );
  const pills = (cfg.pills || []).filter((p) => p.label && p.url);
  const mapPills = pills.filter((p) => isMapsUrl(p.url) && mapsQueryFromUrl(p.url));
  const plainPills = pills.filter((p) => !mapPills.includes(p));
  const pillRow = pills.length > 0 && (
    <div className="mt-6" data-testid="fest-cta-pills">
      {cfg.pills_label && <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#5C5C5C] mb-2">{cfg.pills_label}</div>}
      <div className="flex flex-wrap gap-2">
        {plainPills.map((p, i) => (
          <a key={i} href={p.url} target={/^https?:/i.test(p.url) ? "_blank" : undefined} rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-bold transition-colors hover:text-white" style={{ borderColor: accent, color: accent, "--tw-hover-bg": accent }} onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = accent)} onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")} data-testid={`fest-cta-pill-${i}`}>{p.image && <img src={p.image} alt="" className="h-6 w-6 -ml-2 rounded-full object-cover" />}{p.label}</a>
        ))}
      </div>
      {mapPills.map((p, i) => <DirectionsActions key={`m${i}`} address={mapsQueryFromUrl(p.url)} accent={accent} size="sm" label={p.label} className="mt-3" testId={`fest-cta-pill-directions-${i}`} />)}
    </div>
  );
  const bg = cfg.plain ? undefined : { background: `linear-gradient(135deg, ${accent}15, ${accent}30)` };
  if (cfg.layout === "image-top") {
    return (
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-8" data-testid="fest-cta">
        <div className={cfg.plain ? "" : "rounded-2xl p-8 md:p-10"} style={bg}>
          {image && (
            <div className="flex justify-center mb-8" data-testid="fest-cta-image-column">
              <button type="button" onClick={() => setZoom(image)} className={`relative group cursor-zoom-in ${cfg.image_full ? "w-full" : "w-full max-w-md"}`} aria-label="Enlarge" data-testid="fest-cta-image">
                <img src={image} alt={block.title || ""} className="w-full rounded-2xl shadow-xl object-contain" loading="lazy" />
                <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-full bg-black/60 text-white text-[10px] font-semibold px-2 py-1 group-hover:bg-black/80 transition-colors"><Maximize2 className="h-3 w-3" /> Enlarge</span>
              </button>
            </div>
          )}
          {cfg.headline && (
            <h2 className="text-center font-['Playfair_Display'] font-bold leading-[1.05] text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-6" style={{ color: accent }} data-testid="fest-cta-headline">
              {cfg.headline_url ? (
                /^https?:/i.test(cfg.headline_url)
                  ? <a href={cfg.headline_url} target="_blank" rel="noopener noreferrer" className="hover:underline">{cfg.headline}</a>
                  : <Link to={cfg.headline_url} className="hover:underline">{cfg.headline}</Link>
              ) : cfg.headline}
            </h2>
          )}
          <BlockShell block={block} accent={accent}>
            {cfg.body && <p className="text-[#3a3a3a] max-w-3xl whitespace-pre-line text-base md:text-lg leading-relaxed">{renderInline(cfg.body, accent)}</p>}
            {pillRow}
            {link && <div className="mt-6" data-testid="fest-cta-bottom">{link}</div>}
          </BlockShell>
        </div>
        <Lightbox src={zoom} onClose={() => setZoom(null)} />
      </section>
    );
  }
  if (cfg.layout === "image-bottom") {
    return (
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-8" data-testid="fest-cta">
        <div className={cfg.plain ? "" : "rounded-2xl p-8 md:p-10"} style={bg}>
          <BlockShell block={block} accent={accent}>
            {cfg.body && <p className="text-[#3a3a3a] max-w-3xl whitespace-pre-line text-base md:text-lg leading-relaxed">{renderInline(cfg.body, accent)}</p>}
            {pillRow}
            <div className="mt-8 flex flex-col items-center gap-4" data-testid="fest-cta-image-column">
              {image && (
                <button type="button" onClick={() => setZoom(image)} className={`relative group cursor-zoom-in ${cfg.image_full ? "w-full" : "w-full md:w-[85%]"}`} aria-label="Enlarge" data-testid="fest-cta-image">
                  <img src={image} alt={block.title || ""} className="w-full rounded-2xl shadow-xl object-contain" loading="lazy" />
                  <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-full bg-black/60 text-white text-[10px] font-semibold px-2 py-1 group-hover:bg-black/80 transition-colors"><Maximize2 className="h-3 w-3" /> Enlarge</span>
                </button>
              )}
              {link && <div data-testid="fest-cta-bottom">{link}</div>}
            </div>
          </BlockShell>
        </div>
        <Lightbox src={zoom} onClose={() => setZoom(null)} />
      </section>
    );
  }
  if (cfg.layout === "image-left") {
    return (
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-8" data-testid="fest-cta">
        <div className={cfg.plain ? "" : "rounded-2xl p-8 md:p-10"} style={bg}>
          <BlockShell block={block} accent={accent}>
            <div className="grid gap-8 md:grid-cols-[auto_1fr] md:items-start">
              <div className="flex flex-col items-center gap-4" data-testid="fest-cta-image-column">
                {imgEl}
                {link && <div data-testid="fest-cta-bottom">{link}</div>}
                {cfg.pills_under_button && pillRow}
              </div>
              <div>
                {cfg.body && <p className="text-[#3a3a3a] max-w-2xl whitespace-pre-line text-base md:text-lg leading-relaxed">{renderInline(cfg.body, accent)}</p>}
                {!cfg.pills_under_button && pillRow}
              </div>
            </div>
          </BlockShell>
        </div>
        <Lightbox src={zoom} onClose={() => setZoom(null)} />
      </section>
    );
  }
  return (
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-8" data-testid="fest-cta">
      <div className={`rounded-2xl p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6 ${bottomBtn ? "md:flex-wrap" : ""}`}
        style={{ background: `linear-gradient(135deg, ${accent}15, ${accent}30)` }}>
        <div className="flex-1 min-w-0">
          <BlockShell block={block} accent={accent}>
            {cfg.body && <p className="text-[#3a3a3a] max-w-xl whitespace-pre-line">{renderInline(cfg.body, accent)}</p>}
            {pillRow}
          </BlockShell>
          {image && !bottomBtn && <div className="mt-6">{link}</div>}
        </div>
        {image ? imgEl : (!bottomBtn && link)}
        {bottomBtn && <div className="w-full flex justify-center pt-2" data-testid="fest-cta-bottom">{link}</div>}
      </div>
      <Lightbox src={zoom} onClose={() => setZoom(null)} />
    </section>
  );
};

export default CtaBlock;
